/**
 * テーマプロバイダー
 * ダークモード/ライトモードの切り替えを管理
 */

import React, { createContext, useContext, useEffect } from 'react';
import { MantineColorScheme, useMantineColorScheme } from '@mantine/core';
import { useAuth } from '../../auth/contexts/AuthContext';
import { useUserPreferences, useUpdateUserPreferences } from '../api/hooks';
import type { ThemeMode } from '../types';

interface ThemeContextType {
  colorScheme: MantineColorScheme;
  toggleColorScheme: () => void;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { setColorScheme: setMantineColorScheme, colorScheme: mantineColorScheme } = useMantineColorScheme();
  const { user } = useAuth();
  const { data: preferences, isLoading: preferencesLoading } = useUserPreferences();
  const { mutate: updatePreferences, isPending: isUpdating } = useUpdateUserPreferences();

  // ユーザーの設定を適用
  useEffect(() => {
    if (preferences?.theme && preferences.theme !== 'auto') {
      setMantineColorScheme(preferences.theme);
    }
  }, [preferences, setMantineColorScheme]);

  // テーマを切り替える
  const toggleColorScheme = () => {
    if (!user) return;
    
    const newScheme: ThemeMode = mantineColorScheme === 'light' ? 'dark' : 'light';
    
    // 即座にUIを更新
    setMantineColorScheme(newScheme);
    
    // APIに保存
    updatePreferences({ theme: newScheme });
  };

  return (
    <ThemeContext.Provider 
      value={{ 
        colorScheme: mantineColorScheme,
        toggleColorScheme,
        isLoading: preferencesLoading || isUpdating,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};